import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import {
  DEFAULT_CLAIM_PROCESSING_DEFAULTS,
  type ClaimProcessingDefaults,
} from '../types/claimProcessingDefaults';

/**
 * TASK-2-3 — 클레임 처리 기본 노출값 9종 GET.
 * 클레임 전용 경량 엔드포인트 `GET /api/admin/v1/claims/processing-defaults`
 * (게이트웨이 `/admin/v1/claims/**` → BE RewritePath). v2 envelope `{ success, data, error, meta }`.
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라 orderPolicy 와 동일하게 adminFetch 로 직접 호출.
 * BE 미배포/응답 누락 시 §1 하드 기본값으로 폴백(폼 controlled 유지, raw 미노출).
 */
const PATH = '/admin/v1/claims/processing-defaults';

interface DefaultsEnvelope {
  success: boolean;
  data?: Partial<ClaimProcessingDefaults> | null;
}

/** BE 누락/널 필드를 §1 기본값으로 채워 항상 완전한 9필드를 반환한다. */
const normalize = (
  d: Partial<ClaimProcessingDefaults> | null | undefined,
): ClaimProcessingDefaults => ({
  cancelRestoreStock: d?.cancelRestoreStock ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.cancelRestoreStock,
  cancelRestoreCoupon:
    d?.cancelRestoreCoupon ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.cancelRestoreCoupon,
  cancelGrantFreebie: d?.cancelGrantFreebie ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.cancelGrantFreebie,
  exchangeRestoreCoupon:
    d?.exchangeRestoreCoupon ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.exchangeRestoreCoupon,
  exchangeGrantFreebie:
    d?.exchangeGrantFreebie ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.exchangeGrantFreebie,
  exchangeGrantMileage:
    d?.exchangeGrantMileage ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.exchangeGrantMileage,
  exchangeGrantCouponMileage:
    d?.exchangeGrantCouponMileage ??
    DEFAULT_CLAIM_PROCESSING_DEFAULTS.exchangeGrantCouponMileage,
  refundRestoreStock: d?.refundRestoreStock ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.refundRestoreStock,
  refundRestoreCoupon:
    d?.refundRestoreCoupon ?? DEFAULT_CLAIM_PROCESSING_DEFAULTS.refundRestoreCoupon,
});

export const claimProcessingDefaultsQueryKey = () =>
  ['admin', 'claim-processing-defaults'] as const;

const fetchDefaults = async (): Promise<ClaimProcessingDefaults> => {
  const res = await adminFetch(PATH);
  // 미배포(404) 등은 던지지 않고 §1 기본값으로 폴백 — 노출 전용이라 화면이 항상 기본정책을 보여준다.
  if (!res.ok) return { ...DEFAULT_CLAIM_PROCESSING_DEFAULTS };
  const env = (await res.json()) as DefaultsEnvelope;
  return normalize(env.data);
};

export const useClaimProcessingDefaultsQuery = () =>
  useQuery<ClaimProcessingDefaults>({
    queryKey: claimProcessingDefaultsQueryKey(),
    queryFn: fetchDefaults,
    // 정책 기본값은 자주 안 바뀜 — 신선도보다 안정성 우선(노출 전용).
    staleTime: 5 * 60 * 1000,
  });
